import React, { useState, useCallback, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import MediaItem from "./MediaItem";
import { Button } from "../@/components/ui/button";
import { useToast } from "../@/components/ui/use-toast";
import {
  AWS_CLOUD_FRONT_URL,
  FILE_TYPE_ID,
  MAX_VIDEO_SIZE_MB,
  MAX_IMAGE_SIZE_MB,
  MAX_IMAGE_SIZE_BYTES,
  MAX_VIDEO_SIZE_BYTES,
  MAX_PRODUCT_IMAGES_UPLOAD,
} from "../lib/constants";
import { uploadToS3WithPost } from "../lib/utils";
import { useGetPresignedUrlMutation } from "../api/api";
import { UploadProgress as UploadProgressType } from "../types/upload";
import { RiDeleteBinLine } from "react-icons/ri";
import { FaTimes } from "react-icons/fa";
import { UploadProgress } from "./Video/UploadProgress";

interface MediaUploadProps {
  handleUpload: (payload: any) => void;
  productImages: any[];
  setProductImages: React.Dispatch<React.SetStateAction<any[]>>;
  productId?: string;
  setIsDisabled: React.Dispatch<React.SetStateAction<boolean>>;
  setVideoDetails: React.Dispatch<
    React.SetStateAction<{
      filePath: string;
      isVideoReplaced: boolean;
    }>
  >;
  isForEditVideo?: boolean;
  filePath?: string;
}

const MediaUpload: React.FC<MediaUploadProps> = ({
  handleUpload,
  productImages,
  setProductImages,
  productId,
  setIsDisabled,
  setVideoDetails,
  isForEditVideo = false,
  filePath = "",
}) => {
  const [removeImages, setRemoveImages] = useState<any[]>([]);
  const { toast } = useToast();
  const [uploads, setUploads] = useState<
    Map<
      string,
      { file: File; progress: UploadProgressType; controller: AbortController }
    >
  >(new Map());

  const [deletedVideoPath, setDeletedVideoPath] = useState("");

  const [getPresignedUrl, { isLoading: isGettingPresignedUrl }] =
    useGetPresignedUrlMutation();

  const handleDrop = useCallback(
    (acceptedFiles: File[]) => {
      const currentImages = productImages.filter(
        (img) => img.fileType === FILE_TYPE_ID.IMAGE
      ).length;
      const currentVideo = productImages.some(
        (img) => img.fileType === FILE_TYPE_ID.VIDEO
      );

      const newImages = acceptedFiles.filter((file) =>
        file.type.startsWith("image")
      );
      const newVideos = acceptedFiles.filter((file) =>
        file.type.startsWith("video")
      );

      // Add image size check
      const oversizedImages = newImages.filter(
        (file) => file.size >= MAX_IMAGE_SIZE_BYTES
      );
      if (oversizedImages.length > 0) {
        toast({
          title: "Error",
          description: `Image size must be less than ${MAX_IMAGE_SIZE_MB}MB.`,
        });
        return;
      }

      // Add video size check
      if (newVideos.length > 0) {
        // Convert to MB
        if (newVideos[0].size >= MAX_VIDEO_SIZE_BYTES) {
          toast({
            title: "Error",
            description: `Video size must be less than ${MAX_VIDEO_SIZE_MB}MB.`,
          });
          return;
        }
      }

      // Check for video constraints
      if (newVideos.length > 0 && currentVideo) {
        toast({
          title: "Error",
          description: "You can only upload one video.",
        });
        return;
      }

      if (newVideos.length > 1) {
        toast({
          title: "Error",
          description: "You can only upload one video at a time.",
        });
        return;
      }

      // Check for image constraints
      if (currentImages + newImages.length > MAX_PRODUCT_IMAGES_UPLOAD) {
        toast({
          title: "Error",
          description: `You can upload a maximum of ${MAX_PRODUCT_IMAGES_UPLOAD} images.`,
        });
        return;
      }

      const newMedia = [
        ...newImages.map((file) => ({
          productImageId: URL.createObjectURL(file),
          filePath: URL.createObjectURL(file),
          file,
          fileType: FILE_TYPE_ID.IMAGE,
        })),
        ...newVideos.map((file) => ({
          productImageId: URL.createObjectURL(file),
          filePath: URL.createObjectURL(file),
          file,
          fileType: FILE_TYPE_ID.VIDEO,
        })),
      ];

      setProductImages([...productImages, ...newMedia]);
      const videoFileIndex = [...productImages, ...newMedia].findIndex(
        (item) => item.fileType === 2
      );

      if (newVideos.length > 0) {
        handleVideoUpload(newVideos[0], videoFileIndex);
      }
    },
    [productImages]
  );

  const moveImage = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      const newImages = [...productImages];
      const [draggedImage] = newImages.splice(dragIndex, 1);
      newImages.splice(hoverIndex, 0, draggedImage);
      setProductImages(newImages);
    },
    [productImages]
  );

  const removeImage = (index: number) => {
    const imageToRemove = productImages[index];

    if (!imageToRemove.file) {
      if (imageToRemove.fileType === FILE_TYPE_ID.VIDEO) {
        setVideoDetails({
          filePath: "",
          isVideoReplaced: false,
        });
        setDeletedVideoPath(imageToRemove.filePath);
      }
      setRemoveImages([...removeImages, imageToRemove]);
    } else if (imageToRemove.fileType === FILE_TYPE_ID.VIDEO) {
      // Clear uploads state when removing an unsaved video
      setUploads(new Map());
    }

    const newImages = [...productImages];
    newImages.splice(index, 1);
    setProductImages(newImages);
  };

  const convertToBase64 = (
    file: File
  ): Promise<{ base64: string | null; fileType: string }> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (reader.result) {
          const base64String = reader.result.toString().split(",")[1];
          resolve({ base64: base64String, fileType: file.type });
        } else {
          resolve({ base64: null, fileType: file.type });
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const handleUploadImages = async () => {
    try {
      const base64Images = await Promise.all(
        productImages.map((img) =>
          img.file && img.fileType === FILE_TYPE_ID.IMAGE
            ? convertToBase64(img.file)
            : Promise.resolve({ base64: null, fileType: img.fileType })
        )
      );

      const payload = {
        imagesBlob: productImages.map((img, index) => ({
          productImageId: img.file ? undefined : img.productImageId,
          order: index + 1,
          imageBlob:
            img.file && img.fileType === FILE_TYPE_ID.IMAGE
              ? base64Images[index].base64
              : undefined,
          fileType: img.file
            ? base64Images[index].fileType
            : img.fileType === FILE_TYPE_ID.VIDEO
            ? img.fileType
            : undefined,
          productId: img.file ? undefined : img.productId,
        })),
        removeImages: removeImages.map((img) => ({
          productImageId: img.productImageId,
          filePath: img.filePath,
          id: img.fileType === FILE_TYPE_ID.VIDEO ? img.videoId : undefined,
        })),
      };

      handleUpload(payload);
    } catch (error) {
      console.error("Error uploading files:", error);
    }
  };

  const handleVideoUpload = async (file: File, fileIndex: number) => {
    const existingFailedUpload = Array.from(uploads.entries()).find(
      ([, { progress }]) => progress.status === "error"
    );

    if (existingFailedUpload) {
      const [failedUploadId] = existingFailedUpload;

      // Remove the failed upload
      setUploads((prev) => {
        const newMap = new Map(prev);
        newMap.delete(failedUploadId);
        return newMap;
      });
    }

    if (uploads.size > 0) {
      toast({
        title: "Error",
        description: "You can only upload one video at a time.",
      });
      return;
    }
    const uploadId = Math.random().toString(36).substring(7);
    const controller = new AbortController();

    try {
      // Initialize upload progress
      setUploads((prev) =>
        new Map(prev).set(uploadId, {
          file,
          progress: { progress: 0, status: "uploading" },
          controller,
        })
      );

      let correctedFileName = "";

      if (deletedVideoPath) {
        const fileNameParts = deletedVideoPath.split("/");
        correctedFileName = fileNameParts[fileNameParts.length - 1];
      } else {
        const fileNameParts = file.name.split(".");
        const baseName = fileNameParts.slice(0, -1).join("."); // Handle names with multiple dots
        const extension = fileNameParts[fileNameParts.length - 1];

        // Corrected file name
        correctedFileName = `${baseName}-${uploadId}.${extension}`;
      }

      // Get pre-signed URL (replace with your actual API call)
      const presignedUrl = await getPresignedUrl({
        body: {
          fileName: correctedFileName,
          fileType: file.type,
          index: fileIndex + 1,
          isEdit: deletedVideoPath !== "" ? true : false,
        },
      }).unwrap();

      if (!presignedUrl.url) {
        toast({
          title: "Error",
          description:
            "Failed to get pre-signed URL. Please try again after some time!",
        });
        return;
      }

      // Upload to S3
      setIsDisabled(true);
      await uploadToS3WithPost(
        file,
        presignedUrl.url,
        presignedUrl.fields,
        (progress) => {
          setUploads((prev) => {
            const newMap = new Map(prev);
            const upload = newMap.get(uploadId);
            if (upload) {
              newMap.set(uploadId, {
                ...upload,
                progress: { ...upload.progress, progress },
              });
            }
            return newMap;
          });
        },
        controller.signal
      );

      // Update status to completed
      setUploads((prev) => {
        const newMap = new Map(prev);
        const upload = newMap.get(uploadId);
        if (upload) {
          newMap.set(uploadId, {
            ...upload,
            progress: { progress: 100, status: "completed" },
          });
        }
        return newMap;
      });
      setVideoDetails((prev) => ({
        ...prev,
        filePath: `${AWS_CLOUD_FRONT_URL}/videos/${
          presignedUrl.fields.key.split("/")[1]
        }`,
      }));

      setIsDisabled(false);
    } catch (error: any) {
      setIsDisabled(false);
      if (error.message === "Upload aborted") {
        toast({
          title: "Upload Cancelled",
          description: "You have cancelled the video upload.",
        });
      } else {
        toast({
          title: "Error",
          description: `${
            error?.data?.message ||
            "Failed to upload video. Please try again after some time!"
          }`,
        });
      }
      setUploads((prev) => {
        const newMap = new Map(prev);
        const upload = newMap.get(uploadId);
        if (upload) {
          newMap.set(uploadId, {
            ...upload,
            progress: {
              progress: 0,
              status: "error",
              error: "Upload failed",
            },
          });
        }
        return newMap;
      });
    }
  };

  const handleCancel = (uploadId: string) => {
    const upload = uploads.get(uploadId);
    if (upload) {
      upload.controller.abort();
      setUploads((prev) => {
        const newMap = new Map(prev);
        newMap.delete(uploadId);
        return newMap;
      });

      // Remove the video from productImages
      const videoIndex = productImages.findIndex(
        (image) => image.fileType === FILE_TYPE_ID.VIDEO
      );
      if (videoIndex !== -1) {
        const newImages = [...productImages];
        newImages.splice(videoIndex, 1);
        setProductImages(newImages);
      }
    }
  };

  useEffect(() => {
    handleUploadImages();
  }, [productImages, removeImages]);

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop: handleDrop,
    noClick: true,
    noKeyboard: true,
    accept: {
      "image/jpeg": [],
      "image/png": [],
      "video/*": [".mp4", ".mov"],
    },
    maxFiles: 11,
  });

  return (
    <>
      <DndProvider backend={HTML5Backend}>
        <div className="w-full mx-auto p-4 bg-white shadow-md rounded-md">
          <h3 className="text-lg font-semibold mb-4">
            Media<span className="text-red-500">*</span>
          </h3>
          <div className="flex flex-col gap-2"></div>
          <div
            {...getRootProps()}
            className="mb-4 border-dashed border-2 border-customGrayBorder rounded-lg text-center py-10 sm:py-20 px-4 sm:px-10 bg-grayBackgroundColor"
          >
            <input {...getInputProps()} />
            <p className="text-[#858D9D] text-sm my-2">
              Drag and drop or click "Add Media"
            </p>
            <p className="mt-1 text-xs text-gray-400">
              Support jpg, png, jpeg, mp4, mov
            </p>
            <Button
              type="button"
              onClick={open}
              className="mt-2 px-4 py-2 bg-customGreenLight text-customGreenColor rounded-lg hover:bg-customGreenLight transition-colors"
            >
              Add Media
            </Button>
            <br />
            <span className="text-sm font-medium mb-1 text-customGrayBase">
              Videos (1080x1920px • max file size {MAX_VIDEO_SIZE_MB}MB) <br />
              Images (164x144px • max file size {MAX_IMAGE_SIZE_MB}MB)
            </span>
          </div>
          {productImages?.length > 0 ? (
            <div className="mt-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {productImages?.map((image: any, index) => {
                  const uploadInfo =
                    image.fileType === FILE_TYPE_ID.VIDEO
                      ? Array.from(uploads.values())[0]?.progress
                      : undefined;

                  return (
                    <div className="flex justify-center">
                      <MediaItem
                        key={index}
                        file={image.file}
                        preview={image.filePath}
                        index={index}
                        moveImage={moveImage}
                        removeImage={removeImage}
                        fileType={image.fileType}
                        statusId={image.fileStatusId}
                        uploadProgress={uploadInfo}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500">No media uploaded yet.</p>
          )}
        </div>
      </DndProvider>
      <div className="space-y-4 mt-3">
        {Array.from(uploads.entries()).map(([id, { file, progress }]) => (
          <div key={id} className="flex items-center space-x-4 w-full relative">
            <UploadProgress file={file} progress={progress} />
            {progress.status === "uploading" && (
              <button
                type="button"
                className="text-red-500 hover:text-red-700 absolute top-4 right-4"
                onClick={() => handleCancel(id)}
              >
                <FaTimes />
              </button>
            )}

            {progress.status === "error" && (
              <button
                type="button"
                className="text-red-500 hover:text-red-700 absolute top-4 right-4"
                onClick={() => handleCancel(id)}
              >
                <FaTimes />
              </button>
            )}
          </div>
        ))}
      </div>
    </>
  );
};

export default MediaUpload;
